import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { projetosAPI } from '../../services/inovacaoAPI';
import { mensagemErro, rotuloEstadoProjeto } from './labels';
import { Plus, TrendingUp, AlertTriangle, Clock } from 'lucide-react';

const BTN_ESTILO = {
  background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', padding: '10px 18px',
  textDecoration: 'none', borderRadius: 6, display: 'inline-flex',
  gap: 6, alignItems: 'center', fontWeight: 500,
};

export default function ProjetoLista() {
  const [items, setItems] = useState([]);
  const [estado, setEstado] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { size: 100 };
    if (estado) params.estado = estado;
    // Backend ja devolve progresso e conflitos_total agregados — antes essa
    // tela fazia 2 requests por projeto (fases + conflitos), 200+ pra 100 cards.
    projetosAPI.listar(params)
      .then((r) => setItems(r.items))
      .catch((e) => toast.error(mensagemErro(e, 'Erro ao listar projetos')))
      .finally(() => setLoading(false));
  }, [estado]);

  return (
    <div style={{ padding: 32, background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, color: '#2c3e50' }}>Projetos</h1>
          <p style={{ margin: '4px 0 0', color: '#7f8c8d' }}>
            {items.length} projeto(s). Clique para entrar no workspace
          </p>
        </div>
        <Link to="/inovacao/projetos/novo" style={BTN_ESTILO}>
          <Plus size={16} /> Novo projeto
        </Link>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['', 'planejado', 'em_execucao', 'em_risco', 'pausado', 'concluido', 'cancelado'].map((e) => (
          <button
            key={e}
            onClick={() => setEstado(e)}
            style={{
              padding: '6px 14px',
              background: estado === e ? 'var(--brand, #20a490)' : '#fff',
              color: estado === e ? '#fff' : '#555',
              border: '1px solid ' + (estado === e ? 'var(--brand, #20a490)' : '#ddd'),
              borderRadius: 20, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500,
            }}
          >
            {e ? rotuloEstadoProjeto(e).label : 'Todos'}
          </button>
        ))}
      </div>

      {loading && <p>Carregando…</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
        {items.map((p) => {
          const { pct, concluidas, total } = p.progresso || { pct: 0, concluidas: 0, total: 0 };
          const buffPct = p.buffer_horas_total > 0
            ? Math.round((parseFloat(p.buffer_horas_consumidas) / parseFloat(p.buffer_horas_total)) * 100)
            : 0;
          const conflitos = p.conflitos_total || 0;
          const est = rotuloEstadoProjeto(p.estado);

          return (
            <Link
              key={p.id}
              to={`/inovacao/projetos/${p.id}`}
              style={{
                background: '#fff', borderRadius: 12, padding: 20, textDecoration: 'none',
                color: 'inherit', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                borderLeft: `4px solid ${est.cor}`,
                transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#2c3e50' }}>{p.nome}</h3>
                <span style={{
                  background: est.cor, color: '#fff',
                  padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', whiteSpace: 'nowrap',
                }}>{est.label}</span>
              </div>

              <div style={{ color: '#7f8c8d', fontSize: '0.8rem', marginBottom: 12 }}>
                {p.setor_solicitante?.nome} · {p.responsavel_tecnico?.nome}
              </div>

              {/* Progresso */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 4 }}>
                  <span style={{ color: '#555' }}>Progresso</span>
                  <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                    {concluidas}/{total} fases · {pct}%
                  </span>
                </div>
                <div style={{ height: 8, background: '#ecf0f1', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: pct === 100 ? '#27ae60' : 'var(--brand, #20a490)',
                    transition: 'width 0.3s',
                  }} />
                </div>
              </div>

              {/* Mini stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                <MiniStat icon={<Clock size={14} />} label="Prev. fim" value={p.data_fim_planejada || '—'} />
                <MiniStat
                  icon={<TrendingUp size={14} />}
                  label="Buffer"
                  value={`${buffPct}%`}
                  cor={buffPct > 50 ? '#e67e22' : '#7f8c8d'}
                />
                <MiniStat
                  icon={<AlertTriangle size={14} />}
                  label="Conflitos"
                  value={conflitos}
                  cor={conflitos > 0 ? '#e74c3c' : '#7f8c8d'}
                />
              </div>

              {p.proxima_fase && (
                <div style={{ background: '#f8f9fa', padding: 10, borderRadius: 6, fontSize: '0.82rem' }}>
                  <span style={{ color: '#7f8c8d' }}>
                    {p.proxima_fase.estado === 'planejada' ? 'Próxima: ' : 'Em andamento: '}
                  </span>
                  <b style={{ color: '#2c3e50' }}>{p.proxima_fase.nome}</b>
                  {p.proxima_fase.responsavel && (
                    <span style={{ color: '#7f8c8d' }}> · {p.proxima_fase.responsavel.nome}</span>
                  )}
                </div>
              )}
            </Link>
          );
        })}
        {!loading && !items.length && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: 60, color: '#bdc3c7' }}>
            Nenhum projeto encontrado.
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value, cor = '#7f8c8d' }) {
  return (
    <div style={{ textAlign: 'center', background: '#f8f9fa', padding: 8, borderRadius: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'center', color: cor, marginBottom: 2 }}>{icon}</div>
      <div style={{ fontSize: '0.68rem', color: '#95a5a6', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: cor }}>{value}</div>
    </div>
  );
}
